import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//

/**
 * Array of values fetched from the server.
 * Throughout the code, the word 'configuration' commonly refers to a 'configuration value'.
 * @type {*[]}
 */

var configurationValues;

/**
 * Array of default values fetched from the server.
 */
var configurationDefaults;

/**
 * Number generator to identify each configuration value with a unique id.
 * The 'id' field on each configuration value can be used as the 'key' property of React components in a list.
 * @type {number}
 */
var configurationValuesUniqueIdGenerator = 0;
var edited = false;

/**
 * Sets the configuration values which were directly received from the server.
 * @param values - array of configuration values
 * @param defaults - array of default configuration values
 */
export function setConfigurationValues(values, defaults) {
  initializeConfigurationValues(values, defaults);
  configurationValues = values;
  edited = false;
  if (defaults) {
    configurationDefaults = defaults;
  }
}
export function setEdited(isEdited) {
  edited = isEdited;
}

/**
 * Initializes raw configuration values fetched from the server.
 * @param values - raw configuration values
 * @param defaults - array of raw default configuration values
 */
function initializeConfigurationValues(values, defaults) {
  values.forEach(function (value) {
    return initializeConfigurationValueHierarchy(value);
  });
  if (defaults) {
    defaults.forEach(function (value) {
      return initializeConfigurationValueHierarchy(value);
    });
  }
}

/**
 * Recursively traverses and initializes configuration values fetched from the server.
 */
export function initializeConfigurationValueHierarchy(value, parentValue) {
  value.parent = parentValue;

  // Set a unique id for each configuration value. It may be used to identify React components.
  value.id = configurationValuesUniqueIdGenerator;
  if (!value.children) {
    value.children = [];
  }
  configurationValuesUniqueIdGenerator++;
  if (value.children) {
    value.children.forEach(function (child) {
      initializeConfigurationValueHierarchy(child, value);
    });
  }
}
export function isEdited() {
  return edited;
}
export function clearValues() {
  configurationValues = [];
  edited = false;
}

/**
 * Returns array of all configuration values.
 *
 * Note: do not modify the returned data structure, instead use specialized
 * methods such as setData() or updateChild() to modify configuration values.
 */
export function getConfigurationValues() {
  return configurationValues;
}

/**
 * Pushes a data change on the provided configuration value up the hierarchy which eventually udpates
 * the root [configurationValues] array. This is required in order to avoid
 * changing state of existing objects but rather to clone them.
 * See React documentation on lifting state for more information.
 *
 * @param configuration - unchanged configuration value
 * @param data - updated 'data' to be set on the provided configuration
 */
function pushConfigurationDataChangeToParent(configuration, data) {
  var configurationClone = _objectSpread({}, configuration);
  configurationClone.data = data;
  if (configuration.parent) {
    pushChildConfigurationChangeToParent(configuration.parent, configuration, configurationClone);
  }
  return configurationClone;
}

/**
 * Pushes a 'child' change on the provided parent configuration value up the hierarchy which eventually updates
 * the root [configurationValues] array. This is required in order to avoid changing
 * state of existing objects but rather to clone them.
 * See React documentation on lifting state for more information.
 *
 * @param parentConfiguration - unchanged parent configuration
 * @param prevChildConfiguration - unchanged (previous) child configuration. It can be null if there is no existing value and a brand new one is being added.
 * @param newChildConfiguration - updated child configuration. It can be null if an existing value is being removed.
 * @param unshiftChildrenArray - if a new child configuration is being added (ie. prev does not exist), then should the item be added as the first child or last
 */
function pushChildConfigurationChangeToParent(parentConfiguration, prevChildConfiguration, newChildConfiguration, unshiftChildrenArray) {
  var children;
  var isRootConfiguration = false;
  if (parentConfiguration) {
    children = parentConfiguration.children;
  } else {
    if (configurationValues.includes(prevChildConfiguration)) {
      // root configuration value
      children = configurationValues;
      isRootConfiguration = true;
    }
  }

  //
  // This is not supposed to happen, but it might... try to avoid this type of error...
  if (children === undefined) {
    console.log("Children is undefined");
    return;
  }
  var childrenClone = _toConsumableArray(children);
  var childValueIndex = childrenClone.indexOf(prevChildConfiguration);
  if (childValueIndex !== -1) {
    if (newChildConfiguration) {
      // update
      childrenClone[childValueIndex] = newChildConfiguration;
    } else {
      // remove
      childrenClone.splice(childValueIndex, 1);
    }
  } else {
    if (prevChildConfiguration) {
      throw new Error("Value not found in the children list");
    } else {
      // add
      newChildConfiguration.id = configurationValuesUniqueIdGenerator;
      configurationValuesUniqueIdGenerator++;
      if (unshiftChildrenArray) {
        childrenClone.unshift(newChildConfiguration);
      } else {
        childrenClone.push(newChildConfiguration);
      }
    }
  }
  if (parentConfiguration) {
    var configurationClone = _objectSpread({}, parentConfiguration);
    configurationClone.children = childrenClone;
    childrenClone.forEach(function (child) {
      return child.parent = configurationClone;
    });
    pushChildConfigurationChangeToParent(parentConfiguration.parent, parentConfiguration, configurationClone);
    return configurationClone;
  } else if (isRootConfiguration) {
    // root configuration value reached
    children[childValueIndex] = newChildConfiguration;
    edited = true;
    return null;
  }
}

/**
 * Creates a new configuration value using the defaults received from the server for the provided path.
 *
 * @param path - configuration path
 * @param createChildren - whether to also create children (based on default config) for the newly created configuration value
 * @returns {*} newly created configuration value
 */
export function createForPath(path, createChildren) {
  var pathArray = pathToArray(path);
  if (!pathArray || pathArray.length === 0) {
    return;
  }
  if (!getDefault(pathArray)) {
    throw new Error("It's not possible to create a new value which does not abide by the defined configuration hierarchy. Path: " + path);
  }
  var foundKeyCount = 0;
  for (var i = 1; i < pathArray.length; i++) {
    if (fromPath(pathArray.slice(0, i))) {
      foundKeyCount++;
    } else {
      break;
    }
  }
  var parentConfigValue = fromPath(pathArray.slice(0, foundKeyCount));
  for (var _i = foundKeyCount; _i < pathArray.length; _i++) {
    var newConfigValue = getDefault(pathArray.slice(0, _i + 1));
    newConfigValue.parent = parentConfigValue;

    // If children creation is requested then do not erase children for the last config value (the one identified by the 'path' argument)
    if (!createChildren || _i < pathArray.length - 1) {
      newConfigValue.children = [];
    }
    (parentConfigValue ? parentConfigValue.children : configurationValues).push(newConfigValue);
    parentConfigValue = newConfigValue;
  }
  return fromPath(path);
}

/**
 * Sets/updates 'data' parameter for the provided configuration path.
 * A new configuration value will be initialized in the configuration hierarchy if it doesn't exist.
 *
 * @param path - configuration path
 * @param data - new data
 */
export function setData(path, data) {
  var configValue = fromPath(path, false);
  if (!configValue) {
    var isDefaultValue = data.value === fromPath(path, true).data.value;
    if (!isDefaultValue) {
      configValue = createForPath(path);
    } else {
      return;
    }
  }
  if (configValue) {
    pushConfigurationDataChangeToParent(configValue, data);
  } else {
    throw new Error("Configuration value not found for path: " + path);
  }
}
export function getDataValue(path) {
  var config = fromPath(path);
  return config ? config.data.value : null;
}
export function setDataValue(path, value) {
  //
  // console.log(`>>> Config.setValue(${path}, ${value})`)

  var configValue = fromPath(path, false);
  if (!configValue) {
    var defaultValueFromPath = fromPath(path, true).data.value;
    var isDefaultValue = value === defaultValueFromPath || String(defaultValueFromPath) === String(value);
    if (!isDefaultValue) {
      console.log(">>> *** > Config.setValue - Creating configuration entry for path ".concat(path, " as value trying to be set ").concat(value, " is not the default value"));
      configValue = createForPath(path);
    } else {
      return false;
    }
  }
  if (configValue) {
    //console.log(`>>> Config.setValue - Testing if value of path ${path} is ${value} already (current value ${configValue.data.value})`)
    if (configValue.data.value === value || String(configValue.data.value) === String(value)) {
      //console.log(`>>> Config.setValue - Value of path ${path} is already ${value} (current value ${configValue.data.value}) - no need to set it`)
      // No need to update if existing value is the same
      return false;
    }
    //console.log(`>>> *** > Config.setValue - Value of path ${path} is NOT ${value} (current value ${configValue.data.value}) - will set it`)
    var newData = _objectSpread({}, configValue.data);
    newData.value = value;
    pushConfigurationDataChangeToParent(configValue, newData);
    return true;
  } else {
    throw new Error("Configuration value not found for path: " + path);
  }
}

/**
 * Returns the path for the provided configuration value.
 *
 * @returns {string} The returned path is encoded in form of a a string and each configuration path key
 *                   is delimited with  the ':' character.
 */
export function toPath(configurationValue) {
  var itConfValue = configurationValue;
  var path = [];
  while (itConfValue) {
    if (itConfValue.parent) {
      var parentsChildren = itConfValue.parent.children;
      if (parentsChildren.length > 1) {
        var index = parentsChildren.indexOf(itConfValue);
        if (parentsChildren.find(function (child) {
          return child !== itConfValue && child.key === itConfValue.key;
        })) {
          path.push("[" + index + "]");
        }
      }
    }
    path.push(itConfValue.key);
    itConfValue = itConfValue.parent;
  }
  path = path.reverse();
  return path.join(":");
}

/**
 * Returns an existing configuration for the provided path.
 *
 * @param path - configuration path
 * @param fallbackToDefault - whether to return a (deep cloned) default configuration value if a value doesn't exist
 * @param createIfUndefined - whether to create a new configuration value if one doesn't exist
 * @returns {*} configuration value
 */
export function fromPath(path, fallbackToDefault, createIfUndefined) {
  var confValue = _fromPath(path, configurationValues);
  if (!confValue && fallbackToDefault) {
    return getDefault(path);
  }
  if (!confValue && createIfUndefined) {
    return createForPath(path);
  }
  return confValue;
}
export function pathToArray(path) {
  if (path instanceof Array) {
    return path;
  }
  return path.split(/:|\[|\]/).filter(function (key) {
    return key.length;
  });
}

/**
 * Helper method which returns an existing configuration value identified by the provided path and
 * contained within the hierarchy whose roots are defined in [rootConfigurationValues].
 *
 * @param path - configuration path uniquely identifying the configuration value
 * @param rootConfigurationValues - array of root configuration values (which don't have parents)
 * @returns {*} existing configuration value or null if it doesn't exist
 * @private
 */
function _fromPath(path, rootConfigurationValues) {
  var pathWords = pathToArray(path);
  if (pathWords.length === 0) {
    return null;
  }
  var currentConfigurationValue;
  var _loop = function _loop(_i2) {
    var children = currentConfigurationValue ? currentConfigurationValue.children : rootConfigurationValues;
    if (_i2 + 1 === pathWords.length || isNaN(pathWords[_i2 + 1])) {
      currentConfigurationValue = children.find(function (c) {
        return c.key === pathWords[_i2];
      });
    } else {
      currentConfigurationValue = children[parseInt(pathWords[_i2 + 1])];
      _i2++;
    }
    if (!currentConfigurationValue) {
      i = _i2;
      return "break";
    }
    i = _i2;
  };
  for (var i = 0; i < pathWords.length; i++) {
    var _ret = _loop(i);
    if (_ret === "break") break;
  }
  return currentConfigurationValue;
}

/**
 * Returns a deep cloned default configuration value for the provided path.
 *
 * Retrieving a default configuration value does not support indexes on the path. The indexes are completely ignored and discarded
 * as if they were not there. When you want to retrieve a default value, you must know the exact path such as a:b:c.
 * This is the case because the defaults are kind of like a template/definition of the configuration hierarchy and a path
 * without indexes uniquely identifies a configuration definition in the hierarchy. On the other hand, configuration VALUES (as opposed to DEFINITIONS)
 * can carry duplicates there can be more than one configuration value with path auditor:report:name (for example multiple rows in a table) where
 * each child can be accessed by index like auditor:report:name[0].
 *
 * So think of the defaults as the schema of the configuration hierarchy without any duplicates where the path is the "class name"
 * while the configuration values can have duplicates ("instances").
 *
 * Note: The returned configuration value will not have its 'parent' property set.
 * This is for performance reasons to avoid cloning the entire configuration value hierarchy.
 * If you need to access the 'parent', consider using [fromPath] and pass in createIfUndefined = true
 * which will create the configuration value if it's missing.
 *
 * @param path - configuration path
 * @returns {*} - default configuration value
 */
export function getDefault(path) {
  var pathArray = pathToArray(path);
  // Remove indexes from the path
  pathArray = pathArray.filter(function (conf) {
    return isNaN(conf);
  });
  var defaultConfig = deepClone(_fromPath(pathArray, configurationDefaults));
  if (!defaultConfig) {
    throw "Configuration not found for " + path;
  }
  return defaultConfig;
}

/**
 * Deep clone a configuration value.
 * Note: the 'data' parameter is cloned however, if the 'data' parameter contains other objects, then those will not be cloned.
 *
 * @param configurationValue - configuration value to clone
 * @param parent - optional parent to be set on the cloned configuration value. Note: the parent will not be cloned.
 * @returns {*} cloned copy
 */
export function deepClone(configurationValue, parent) {
  if (!configurationValue) {
    return configurationValue;
  }
  var configurationValueClone = _objectSpread({}, configurationValue);
  configurationValueClone.parent = parent;
  if (configurationValueClone.data) {
    configurationValueClone.data = _objectSpread({}, configurationValueClone.data);
  }
  configurationValueClone.children = configurationValueClone.children.map(function (child) {
    return deepClone(child, configurationValueClone);
  });
  return configurationValueClone;
}

/**
 * Returns a child configuration value identified by the key.
 * If the child does not exist, it will be created unless
 * [fallbackToDefault] is true.
 */
export function getChild(parentConfigurationValue, key, fallbackToDefault) {
  var child = parentConfigurationValue.children.find(function (v) {
    return v.key === key;
  });
  if (!child) {
    var path = toPath(parentConfigurationValue) + ":" + key;
    if (fallbackToDefault) {
      child = getDefault(path);
    } else {
      child = createForPath(path);
    }
  }
  return child;
}
export function swapChildren(parentConfiguration, childIndexA, childIndexB) {
  var configurationClone = _objectSpread({}, parentConfiguration);
  var originalChildren = configurationClone.children;
  if (childIndexA < 0 || childIndexA >= originalChildren.length) {
    console.log("Unable to swap children. Index out of bounds: " + childIndexA);
    return false;
  }
  if (childIndexB < 0 || childIndexB >= originalChildren.length) {
    console.log("Unable to swap children. Index out of bounds: " + childIndexB);
    return false;
  }
  var childrenClone = _toConsumableArray(originalChildren);

  // Swap indexes
  var temp = childrenClone[childIndexB];
  childrenClone[childIndexB] = childrenClone[childIndexA];
  childrenClone[childIndexA] = temp;
  configurationClone.children = childrenClone;
  childrenClone.forEach(function (child) {
    return child.parent = configurationClone;
  });
  pushChildConfigurationChangeToParent(parentConfiguration.parent, parentConfiguration, configurationClone);
  return true;
}
export function copyChildValue(parent, targetChildConfigKey, sourceChildConfigKey) {
  var sourceConfig = getChild(parent, sourceChildConfigKey);
  var targetConfig = getChild(parent, targetChildConfigKey);
  return setDataValue(toPath(targetConfig), sourceConfig.data.value);
}
export function setChildValue(parent, childConfigKey, value) {
  var childConfig = getChild(parent, childConfigKey);
  return setDataValue(toPath(childConfig), value);
}

/**
 * Updates a child configuration value.
 * See [pushChildConfigurationChangeToParent]
 */
export function updateChild(parentConfigValue, prevConfigValue, newConfigValue) {
  pushChildConfigurationChangeToParent(parentConfigValue, prevConfigValue, newConfigValue);
}

/**
 * Adds a child configuration value.
 * See [pushChildConfigurationChangeToParent]
 * @param addAsFirstChild whether the new child should be added as the first item in the children list.
 *                        If false, it will be added as the last item.
 * @returns new parent configuration value containing the new child
 */
export function addChild(parentConfigValue, newConfigValue, addAsFirstChild) {
  return pushChildConfigurationChangeToParent(parentConfigValue, null, newConfigValue, addAsFirstChild);
}

/**
 * Removes a child configuration value at the specified index.
 * See [pushChildConfigurationChangeToParent]
 */
export function deleteChild(path, index) {
  var confValue = fromPath(path);
  var childConfValue = confValue.children[index];
  pushChildConfigurationChangeToParent(confValue, childConfValue, null);
}

/**
 * Creates an array of configuration value data transfer objects ready to be sent to the server.
 * @param values 0 configuration values or null if all values should be sent
 * @param customizeDtoFunction - optional function which accepts two parameters: dto and configuration.
 *                                It allows to intercept the configuration value dto and customize its state
 * @returns array of configuration value dtos
 */
export function buildConfigurationValueDtos(values, customizeDtoFunction) {
  if (!values) {
    values = configurationValues;
  }
  if (values) {
    var dtos = [];
    values.forEach(function (value) {
      var dto = {
        key: value.key
      };
      if (value.children) {
        dto.children = buildConfigurationValueDtos(value.children, customizeDtoFunction);
      }
      if (value.data && value.data.value !== null) {
        dto.data = value.data.value;
      }
      if (customizeDtoFunction) {
        customizeDtoFunction(dto, value);
      }
      dtos.push(dto);
    });
    return dtos;
  }
  return null;
}